/**
 * Addon catalog — modular upsells stacked on top of the base subscription.
 *
 * Each addon corresponds to:
 *   - A Stripe Product + Prices (created in the Stripe dashboard)
 *   - A row in `user_addons` when active (mirror of Stripe state)
 *   - A `useUserAddons().has(addon_key)` check in the app to gate features
 *
 * To add a new addon:
 *   1. Create the Stripe Product + monthly/yearly Prices, get the price IDs
 *   2. Add an entry below
 *   3. Wire feature checks in components: `if (!has("addon_key")) return <UpgradePrompt/>`
 *   4. Mirror the price IDs into supabase/functions/stripe-webhook/index.ts
 *      (ADDON_PRICE_TO_KEY) and supabase/functions/manage-addon/index.ts
 *
 * Pricing IDs here are NOT secret — Stripe price IDs are visible to clients
 * during checkout. They're hardcoded so frontend + edge functions agree.
 */

export const ADDONS = {
  // BAM Film Studio+ — direct video upload, AFS possession tagging,
  // workout tagging, native fast playback, private storage. Gates the
  // upload flow + Advantage Flow tagger inside the (free) film studio.
  film_studio: {
    key: "film_studio",
    name: "BAM Film Studio+",
    tagline: "Upload your own film. Privately.",
    description:
 "Game tape, workouts, practice: uploaded direct, never on YouTube. " +
      "Plus Advantage Flow possession tagging, workout tagging, native fast " +
      "playback, and private share links for non-members.",
    bullets: [
      "Direct video upload (any source)",
      "50 hours of private storage",
      "Advantage Flow possession tagging on your own games",
 "Workout tagging: drill-by-drill execution notes",
      "Native fast playback, no YouTube branding",
      "Private share links for non-members",
    ],
    prices: {
      monthly: {
        stripe_price_id: "price_1TTAwjG8VcQAE5BptL7ZGId5",
        amount_cents: 1999,
        interval: "month",
        display: "$19.99/mo",
      },
      yearly: {
        stripe_price_id: "price_1TTAzZG8VcQAE5BpHLFiSMxu",
        amount_cents: 19900,
        interval: "year",
        display: "$199/yr",
        savings_label: "save $40 (~17%)",
      },
    },
    // Storage cap in minutes for the addon's quota system.
    quota: {
      storage_minutes: 50 * 60, // 50 hours
      file_max_minutes: 120,    // 2 hours per file
      file_max_bytes: 2 * 1024 * 1024 * 1024, // 2 GB per file
    },
    // What this addon unlocks. Components read these to decide whether
    // to show upgrade prompts vs. real UI.
    unlocks: [
      "film_upload",
      "advantage_flow",
      "workout_tagging",
      "private_share_link",
      "native_player",
    ],
    upsell_packs: [
      {
        key: "storage_25",
        name: "+25 hours storage",
        price_display: "+$9.99/mo",
        // stripe_price_id: ... (added in V2)
      },
    ],
  },

  // Auto-AFS — AI auto-tags possessions on uploaded games. Replaces
  // the 3-10 min/game manual AFS tagging workflow. Pre-launch, no
  // Stripe price yet; allowlist-only beta (Coleman + invited coaches).
  // See launch-prep/auto-tracking/ for the full product spec.
  auto_afs: {
    key: "auto_afs",
    name: "Auto-AFS",
    tagline: "AI tags every possession. You just review.",
    description:
 "Upload a game, get a full Advantage Flow session back in 10-30 minutes: " +
      "every possession tagged, every shot detected, all the modern basketball " +
      "stats coaches actually want. Review and correct in the AFS UI you already know.",
    bullets: [
      "Auto-tagged AFS Standard mode on every game",
      "Choose your advantage definition: Conservative / Standard / Aggressive",
      "Per-player Team + Player Dashboards out of the box",
      "Modern Metrics: Shot Diet, Spacing, Pace, Player Load, Heat Maps",
      "Cross-game trend analytics (coming soon)",
 "Manual jersey assignment (60 sec/game): no flaky OCR",
    ],
    // Pricing locked once we see real per-clip GPU cost from the first
    // 5 beta coaches. Spec lives in launch-prep/auto-tracking/01-metrics-and-tiers.md.
    prices: {
      // monthly + yearly intentionally omitted at v1 — allowlist-only.
    },
    unlocks: [
      "auto_afs_jobs",     // the "Auto-Tag" button on FilmWatch
      "auto_afs_dashboards", // future: cross-session analytics surface
    ],
  },

  // Coach's Library — replaces the legacy $54.99 Pro plan's drill-upload
  // gate. Free tier still gets 5 uploads at 3:00 each; this addon lifts
  // the cap to unlimited uploads with longer max duration. Distinct from
  // Film Studio+ which is video-FILM (game tape, workouts) — Coach's
  // Library is your DRILL library (the "Save a Drill to Your Library"
  // modal in the practice pages).
  //
  // Monthly only at launch — yearly added once we see real demand.
  coach_library: {
    key: "coach_library",
    name: "Coach's Library",
    tagline: "Make the platform your own.",
    description:
      "Build out your personal drill library. Upload unlimited drills, with " +
      "longer-form clips, and customize how the platform shows your work.",
    bullets: [
      "Unlimited drill uploads (free tier capped at 5)",
      "Up to 10:00 per drill (free tier capped at 3:00)",
      "Custom organization for your drill library",
      "Priority for upcoming personalization features",
    ],
    prices: {
      monthly: {
        stripe_price_id: "price_1TU6JPG8VcQAE5BpOaIBP3uM",
        amount_cents: 1499,
        interval: "month",
        display: "$14.99/mo",
      },
      // Yearly intentionally omitted — monthly only at launch.
    },
    unlocks: [
      "custom_drill_unlimited",  // lifts the 5-upload cap
      "custom_drill_extended",   // 3:00 → 10:00 max duration
      "custom_drill_organization", // future: folders, tags, search
    ],
  },
};

// Convenience exports for components that just need one piece.
export const ADDON_KEYS = Object.keys(ADDONS);

// What features are gated behind each addon. Inverts the `unlocks` lists
// above — "given a feature, which addon do I need?" lookups.
export const FEATURE_TO_ADDON = (() => {
  const map = {};
  for (const key of ADDON_KEYS) {
    for (const unlock of ADDONS[key].unlocks || []) {
      map[unlock] = key;
    }
  }
  return map;
})();

/**
 * Beta feature allowlist. Until an addon is GA, only emails in this list
 * see the addon UI / can subscribe to it. Server-side checks enforce the
 * same allowlist in the manage-addon edge function so the gate isn't
 * just cosmetic.
 *
 * Once an addon ships, REMOVE its key from `addonsLockedToAllowlist`
 * (or set it to undefined) — the addon then appears for all coaches.
 */
export const ADMIN_BETA_ALLOWLIST = [
  "coleman@byanymeansbball.com",
];

export const addonsLockedToAllowlist = {
 film_studio: true, // pre-launch: only allowlist sees it
 auto_afs: true, // private beta: Coleman + invited coaches only
  // coach_library: launched 2026-05-06 — Stripe price wired (price_1TU6JPG8VcQAE5BpOaIBP3uM)
};

export function isAddonGatedToAllowlist(addonKey) {
  return !!addonsLockedToAllowlist[addonKey];
}

export function isAllowedBetaUser(userEmail) {
  if (!userEmail) return false;
  return ADMIN_BETA_ALLOWLIST.includes(String(userEmail).toLowerCase());
}
